import { createSlice } from '@reduxjs/toolkit';
import type { Dispatch } from '@reduxjs/toolkit';
import { actionOnMessage } from '@skynet/data-ws-mm';
import { parseResponse } from '@skynet/data-ws-core';

import { listen } from './actions';
import type { ArbitrageState, JsonArbitrageAccountsSuccess, JsonArbitrageSuccess } from './types';
import { ArbitrageCommands } from './types';

const allowedCommands = Object.values(ArbitrageCommands);

export const initialState: ArbitrageState = {
  list: [],
  error: null,
  details: null,
  // Data is always requested on mount, so start in a loading state to avoid an
  // empty-state flash before the initial Listen command is dispatched.
  requestStatus: 'pending',
};

export const arbitrageSlice = createSlice({
  name: 'arbitrage',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listen, (state, action) => {
      // Mark the list as loading when the initial Listen command is sent; pings
      // (isInitCall === false) must not re-trigger the loading state.
      if (action.meta.isInitCall) {
        state.requestStatus = 'pending';
      }
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse(action.payload, allowedCommands);

      if (skipProcessing || !json) return;

      const { command } = json;

      if (error) {
        state.error = error;
        state.requestStatus = 'error';
        return;
      }

      switch (command) {
        case ArbitrageCommands.Arbitrage: {
          const { list } = json as JsonArbitrageSuccess;
          state.requestStatus = 'success';
          state.list = list;
          break;
        }
        case ArbitrageCommands.ArbitrageAccounts: {
          const { id, accounts } = json as JsonArbitrageAccountsSuccess;
          state.requestStatus = 'success';
          state.details ??= {};
          state.details[id] = accounts;
          break;
        }
        default:
      }
    });
  },
});

export const arbitrageActions = {
  listen,
};

/** Any action created by {@link arbitrageActions}. */
export type ArbitrageAction = ReturnType<(typeof arbitrageActions)[keyof typeof arbitrageActions]>;

/**
 * A dispatch narrowed to this slice's actions, for consumers that only send the
 * arbitrage `Listen` (`useDispatch<ArbitrageDispatch>()`). The app's root
 * dispatch satisfies it.
 */
export type ArbitrageDispatch = Dispatch<ArbitrageAction>;

export default arbitrageSlice.reducer;
